
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/components/ui/use-toast";
import { saveCharterSignatory } from "@/lib/supabase/charter";
import { supabase } from "@/integrations/supabase/client";
import { charteFormSchema, CharteFormValues } from "./types";

export const useCharteForm = (onSuccess: () => void) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    console.log('[DEBUG CharteSignatureForm] Composant monté');
  }, []);

  const form = useForm<CharteFormValues>({
    resolver: zodResolver(charteFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      acceptCharter: false,
      isFrenchCitizen: false
    }
  });

  // Vérifier si l'email existe déjà
  const checkIfEmailExists = async (email: string): Promise<boolean> => {
    try {
      console.log('[DEBUG CharteSignatureForm] Vérification si email existe:', email);
      const { data, error } = await supabase.rpc('check_charter_email_exists', {
        check_email: email
      });
      
      if (error) {
        console.error('[DEBUG CharteSignatureForm] Erreur lors de la vérification d\'email:', error);
        return false;
      }
      
      console.log('[DEBUG CharteSignatureForm] Résultat de la vérification:', data);
      return data === true;
    } catch (error) {
      console.error('[DEBUG CharteSignatureForm] Exception lors de la vérification d\'email:', error);
      return false;
    }
  };

  async function onSubmit(values: CharteFormValues) {
    console.log('[DEBUG CharteSignatureForm] Début onSubmit avec les valeurs:', values);
    setIsSubmitting(true);
    try {
      // Vérifier d'abord si l'email existe déjà
      const emailExists = await checkIfEmailExists(values.email);
      if (emailExists) {
        console.log('[DEBUG CharteSignatureForm] Email déjà utilisé:', values.email);
        // Afficher un toast au lieu d'un dialogue
        toast({
          title: "Signature déjà enregistrée",
          description: "Cette adresse email a déjà été utilisée pour signer la charte républicaine. Merci de votre engagement !",
          duration: 5000 // 5 secondes
        });
        setIsSubmitting(false);
        return;
      }
      
      console.log('[DEBUG CharteSignatureForm] Email non trouvé, poursuite de la soumission');
      console.log('[DEBUG CharteSignatureForm] Préparation des données pour saveCharterSignatory');
      const signatoryData = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        acceptCommunications: values.isFrenchCitizen // utiliser le même champ pour stocker l'info
      };
      console.log('[DEBUG CharteSignatureForm] Données préparées:', signatoryData);
      console.log('[DEBUG CharteSignatureForm] Appel de saveCharterSignatory...');
      const result = await saveCharterSignatory(signatoryData);
      console.log('[DEBUG CharteSignatureForm] Résultat de saveCharterSignatory:', result);
      setEmailSent(true);
      toast({
        title: "Vérifiez votre boîte email !",
        description: "Un email de vérification vient de vous être envoyé. Veuillez cliquer sur le lien qu'il contient pour confirmer votre signature."
      });
      console.log('[DEBUG CharteSignatureForm] Appel de onSuccess et réinitialisation du formulaire');
      onSuccess();
      form.reset();
    } catch (error) {
      console.error('[DEBUG CharteSignatureForm] Erreur complète lors de la soumission:', error);
      toast({
        title: "Une erreur est survenue",
        description: error instanceof Error ? error.message : "Impossible d'enregistrer votre signature pour le moment. Veuillez réessayer plus tard.",
        variant: "destructive"
      });
    } finally {
      console.log('[DEBUG CharteSignatureForm] Fin du traitement (finally)');
      setIsSubmitting(false);
    }
  }

  return {
    form,
    isSubmitting,
    emailSent,
    onSubmit,
  };
};
